import {
  AnimatePresence,
  motion,
  useElementScroll,
  useSpring,
  useTransform,
  Variants,
} from "framer-motion";
import { AnimationDefinition } from "framer-motion/types/render/utils/animation";
import { useRef } from "react";
import InView from "react-intersection-observer";
import { useLockBodyScroll, useToggle } from "react-use";
import styled, { css } from "styled-components";
import { fill } from "../../styles/utils";
import { Box } from "../Box";
import { Portal } from "../Portal";
import { Text } from "../Text";
import { DateCycle } from "./DateCycle";
import { TimelineScrollPrompt } from "./TimelineScrollPrompt";

interface TimelineIntroProps {
  onComplete: () => any;
  onScrollTrigger: () => any;
}

const Cover = styled(motion.div)`
  ${fill("fixed")}
  z-index: 5;
  height: 100vh;
  overflow: auto;

  /* width */
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Wrapper = styled(motion.div)`
  ${fill("fixed")}
  z-index: 4;
  pointer-events: all;
  height: 100vh;
`;

const Inner = styled.div`
  ${fill()}
  z-index: 500;
  pointer-events: all;
  height: 100vh;
`;

const BG = styled(motion.div)`
  ${fill()}
  z-index: -1;
  background-color: ${({ theme }) => theme.colors.salmon};
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const Move = styled(motion.div)`
  height: 100%;
`;

const ScrollPromptContainer = styled.div`
  position: absolute;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      bottom: 32px;
    }
  `}
`;

const centerVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
    // transformOrigin: "left",
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.4, duration: 0.8, type: "tween" },
  },
  exit: {
    opacity: 0,
    scale: 2,
    transition: { delay: 0.8, duration: 0.8 },
  },
};

const scollPromptVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
    // transformOrigin: "left",
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: { delay: 0, duration: 2, type: "tween" },
  },
  exit: {
    opacity: 0,
    scale: 2,
    transition: { delay: 0.8, duration: 0.8 },
  },
};

export const TimelineIntro: React.FC<TimelineIntroProps> = ({
  onComplete,
  onScrollTrigger,
}) => {
  const scrollRef = useRef(null);

  const [showDate, setShowDate] = useToggle(false);

  const [isDone, setIsDone] = useToggle(false);

  const { scrollYProgress } = useElementScroll(scrollRef);

  const physics = { damping: 15, mass: 0.27, stiffness: 55 }; // easing of smooth scroll
  const spring = useSpring(scrollYProgress, physics); // apply easing to the negative scroll value

  const opacity = useTransform(spring, [0.05, 0.2], [1, 1]);
  const scale = useTransform(spring, [0.05, 0.2], [1, 1.1]);
  const y = useTransform(spring, [0, 1], ["0vw", "-40vw"]);

  const handleInViewChange = (inView: any, entry: any) => {
    if (!inView && entry.time > 1000) {
      onScrollTrigger();
    }
  };

  const handleAnimationComplete = (def: AnimationDefinition) => {
    if (def === "animate") {
      setShowDate(true);
    }
  };

  const handleComplete = () => {
    console.log("complete");
    setIsDone(true);
    onComplete();
  };

  return (
    <>
      <Cover ref={scrollRef}>
        <Box height="200vh">
          <Box position="absolute" top={"20vh"} width={"100%"}>
            <InView onChange={handleInViewChange}></InView>
          </Box>
        </Box>
      </Cover>
      <Wrapper id="timeline-intro">
        <BG
          initial={false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        />
        <Inner>
          <Move style={{ y }}>
            <FlexContainer>
              <Box pb={5} width="60vw">
                <motion.div
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={centerVariants}
                >
                  <motion.div
                    style={{ opacity, scale }}
                    onAnimationComplete={handleAnimationComplete}
                  >
                    <Text variant="h1">History of the</Text>
                    <Text variant="h1">pharmacy</Text>
                  </motion.div>
                  <DateCycle onComplete={handleComplete} />
                </motion.div>
                {/* <motion.div>

                </motion.div> */}
              </Box>
            </FlexContainer>
          </Move>
          <ScrollPromptContainer>
            {isDone && (
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={scollPromptVariants}
                transition={{ duration: 0.8 }}
              >
                <TimelineScrollPrompt />
              </motion.div>
            )}
          </ScrollPromptContainer>
        </Inner>
      </Wrapper>
    </>
  );
};
